// ---------------------------------------------------------------------------
// Survey Renderer (editor key: Survey)
// ---------------------------------------------------------------------------
// Renders a single-question, single-select survey as a static <form>. Options
// come from a newline-delimited textarea; duplicates and blank lines are
// dropped, and the option count is capped at 20 so a runaway paste cannot
// blow up the rendered card. The `action` URL routes through the URL
// safelist; if omitted (or rejected by the safelist) the form publishes as
// `disabled` so the page never silently posts to `#`. Each option uses a
// real <input type="radio"> with the option text as both the label and the
// submitted value (escaped via attr()). The submitted field name is
// validated against a safe pattern so a malicious textarea cannot inject
// stray attributes.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface SurveyProps {
  question?: string;
  options?: string;
  buttonLabel?: string;
  action?: string;
  questionName?: string;
}

const DEFAULT_QUESTION = "What matters most to you?";
const DEFAULT_BUTTON_LABEL = "Submit Answer";
const DEFAULT_QUESTION_NAME = "survey_answer";

const MAX_OPTIONS = 20;
const SAFE_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/;

function parseOptions(value: unknown): string[] {
  if (typeof value !== "string") return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of value.split("\n")) {
    const trimmed = raw.trim();
    if (!trimmed) continue;
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    out.push(trimmed);
    if (out.length >= MAX_OPTIONS) break;
  }
  return out;
}

function nonEmpty(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  return trimmed ? trimmed : fallback;
}

function safeName(value: unknown, fallback: string): string {
  if (typeof value !== "string") return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return SAFE_NAME_PATTERN.test(trimmed) ? trimmed : fallback;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as SurveyProps;

  const options = parseOptions(p.options);
  if (options.length === 0) {
    return "<!-- survey missing options -->";
  }

  const question = nonEmpty(p.question, DEFAULT_QUESTION);
  const buttonLabel = nonEmpty(p.buttonLabel, DEFAULT_BUTTON_LABEL);
  const questionName = safeName(p.questionName, DEFAULT_QUESTION_NAME);

  const rawAction = typeof p.action === "string" ? p.action.trim() : "";
  const sanitizedAction = rawAction ? url(rawAction) : "";
  const hasAction = sanitizedAction.length > 0 && sanitizedAction !== "#";
  const disabledAttrs = hasAction ? "" : ' disabled aria-disabled="true"';

  const formAttrs = hasAction
    ? `action="${sanitizedAction}" method="post"`
    : `data-ease-survey="unconfigured"`;

  const optionsHtml = options
    .map((option, index) => {
      const optionId = `${questionName}-${index + 1}`;
      return `      <label class="ease-survey__option" for="${attr(optionId)}">
        <input class="ease-survey__radio" id="${attr(optionId)}" type="radio" name="${attr(questionName)}" value="${attr(option)}" required${disabledAttrs}>
        <span class="ease-survey__option-label">${text(option)}</span>
      </label>`;
    })
    .join("\n");

  return `<section class="ease-section ease-survey">
  <div class="ease-survey__inner">
    <h2 class="ease-survey__question">${text(question)}</h2>
    <form class="ease-survey__form" ${formAttrs}>
      <fieldset class="ease-survey__options" role="radiogroup">
${optionsHtml}
      </fieldset>
      <button class="ease-survey__submit ease-btn ease-btn-primary" type="submit"${disabledAttrs}>${text(buttonLabel)}</button>
    </form>
  </div>
</section>`;
}

registerRenderer("survey", { render });
